import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import { DEFAULT_PANJANGAM, getFollowOnLettersForName, getLunarMansion, getLunarMansionIndex, getRelatedLetters, getStartingLettersForName, isImplementedPanjangam, } from '../astro/index.js';
import { DEFAULT_NUMEROLOGY, getBirthNumber, isImplementedNumerology, NAME_NUMBERS, } from '../numerology/index.js';
import { getDefaultTimezone } from '../timezones/index.js';
// Extended here rather than only in the entry points, since the astro helpers
// below are shared by the browser bundle and the server bundle.
dayjs.extend(utc);
dayjs.extend(timezone);
export const sentenseCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};
/**
 * Birth date as an absolute instant. `tob` is a wall-clock value, so it only
 * identifies a moment once it is read in the selected timezone.
 */
export const getBirthDate = (tob, tz) => {
    if (!tob || !tz) {
        return null;
    }
    try {
        const date = dayjs.tz(tob, tz);
        return date.isValid() ? date.toDate() : null;
    }
    catch {
        // dayjs.tz throws on an unknown timezone name.
        return null;
    }
};
export const getStartingLettersForFilter = (filter) => {
    if (filter.startsWithMode === 'manual') {
        return filter.startsWith;
    }
    if (filter.startsWithMode === 'auto') {
        const date = getBirthDate(filter.tob, filter.tz);
        if (date) {
            const lunarMansionIndex = getLunarMansionIndex(date, filter.panjangam);
            const letters = [
                ...getStartingLettersForName(lunarMansionIndex, 'en'),
                ...getStartingLettersForName(lunarMansionIndex, 'ta'),
                ...(filter.followOnLetters
                    ? getFollowOnLettersForName(lunarMansionIndex)
                    : []),
            ];
            return filter.relatedLetters
                ? [...letters, ...getRelatedLetters(letters)]
                : letters;
        }
    }
    return undefined;
};
/**
 * The day of the month has to be read in the timezone of birth, not wherever
 * the server runs. Only auto mode asks for a date of birth; anywhere else
 * `tob` is just today's date and the number would be confidently wrong.
 */
export const getBirthNumberFor = (startsWithMode, tob, tz) => {
    if (startsWithMode !== 'auto' || !tob || !tz) {
        return null;
    }
    try {
        const date = dayjs.tz(tob, tz);
        return date.isValid() ? getBirthNumber(date.date()) : null;
    }
    catch {
        return null;
    }
};
const parseNameNumbers = (value) => {
    if (!value) {
        return undefined;
    }
    const numbers = [
        ...new Set(value
            .split(',')
            .map((item) => Number(item.trim()))
            .filter((item) => NAME_NUMBERS.includes(item))),
    ].sort((a, b) => a - b);
    return numbers.length ? numbers : undefined;
};
export const getStateFromParams = (params) => {
    const panjangam = params.get('panjangam');
    const numerology = params.get('numerology');
    const twinNames = params.get('twinNames') === 'true';
    const base = {
        gender: params.get('gender') || undefined,
        twinNames,
        // `twin_names` has no religion column, so the filter cannot be applied
        // to a twin search. Dropping it here keeps the title, the access token
        // and the PDF header from claiming a religion the query never used.
        religion: twinNames
            ? undefined
            : params.get('religion') || undefined,
        tob: params.get('tob') || dayjs().format('YYYY-MM-DDTHH:mm'),
        tz: params.get('tz') || getDefaultTimezone(),
        // Anything unknown (or not implemented yet) falls back to the default,
        // so an unsupported method can never reach the calculation.
        panjangam: panjangam && isImplementedPanjangam(panjangam)
            ? panjangam
            : DEFAULT_PANJANGAM,
        numerology: numerology && isImplementedNumerology(numerology)
            ? numerology
            : DEFAULT_NUMEROLOGY,
        nameNumbers: parseNameNumbers(params.get('nameNumbers')),
        followOnLetters: params.get('followOnLetters') === 'true',
        relatedLetters: params.get('relatedLetters') === 'true',
    };
    const startsWithMode = params.get('startsWithMode') ||
        'none';
    if (startsWithMode === 'manual') {
        return {
            ...base,
            startsWithMode,
            startsWith: params.get('startsWith')?.split(',') || [],
        };
    }
    return { ...base, startsWithMode };
};
export const getDocumentTitleByFilter = (filter) => {
    const documentTitle = [];
    if (filter.twinNames) {
        if (filter.gender) {
            if (filter.religion) {
                documentTitle.push(`Twin Names for ${sentenseCase(filter.religion)} ${sentenseCase(filter.gender)}s`);
            }
            else {
                documentTitle.push(`Twin Names for ${sentenseCase(filter.gender)}s`);
            }
        }
        else if (filter.religion) {
            documentTitle.push(`${sentenseCase(filter.religion)} Names`);
        }
        else {
            documentTitle.push('Twin Names');
        }
    }
    else {
        if (filter.gender) {
            if (filter.religion) {
                documentTitle.push(`${sentenseCase(filter.religion)} ${sentenseCase(filter.gender)} Names`);
            }
            else {
                documentTitle.push(`${sentenseCase(filter.gender)} Names`);
            }
        }
        else if (filter.religion) {
            documentTitle.push(`${sentenseCase(filter.religion)} Names`);
        }
    }
    if (filter.startsWithMode === 'manual' &&
        filter.startsWith &&
        filter.startsWith.length > 0) {
        documentTitle.push(`Starting with ${filter.startsWith}`);
    }
    else if (filter.startsWithMode === 'auto') {
        const date = getBirthDate(filter.tob, filter.tz);
        if (date) {
            const lunarMansionIndex = getLunarMansionIndex(date, filter.panjangam);
            const lunarMansion = getLunarMansion(lunarMansionIndex, 'en');
            documentTitle.push(`For ${lunarMansion} Nakshatra`);
        }
    }
    documentTitle.push('Tamil Baby Names');
    return documentTitle.join(' | ');
};
//# sourceMappingURL=index.js.map